import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSelector } from "react-redux";
import {
  FaHome,
  FaUtensils,
  FaUmbrellaBeach,
  FaShieldAlt,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaCheckCircle,
} from "react-icons/fa";

const AboutUs = () => {
  const hotel = useSelector((state) => state.hotel.data);
  const hotelName = hotel?.name || "Ganesh Holiday Homes";
  const contact = hotel?.contact || {
    address: "Narayan Pakhadi Rd, Narayan Pakhadi, Shrivardhan-402110, Maharashtra, India",
    phone: "+91 9673150591",
    email: "ganeshholidayhomes@gmail.com",
  };

  return (
    <div className="d-flex flex-column min-vh-100 bg-light">
      <Navbar />

      {/* Hero Banner */}
      <div
        style={{
          background: "linear-gradient(135deg, #07101e 0%, #0b192c 50%, #1e3e62 100%)",
          color: "#ffffff",
          padding: "50px 0 60px 0",
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <div className="container text-center">
          <div className="d-inline-flex align-items-center gap-2 bg-white bg-opacity-10 text-white px-3 py-1 rounded-pill small fw-semibold mb-3 border border-white border-opacity-15">
            <span>🏡 Direct Standalone Accommodation • Shrivardhan, Maharashtra</span>
          </div>
          <h1 className="h1 fw-bold mb-2 font-heading text-white" style={{ fontSize: "2.5rem" }}>
            About {hotelName}
          </h1>
          <p className="text-white-50 mb-0 mx-auto" style={{ maxWidth: "700px", fontSize: "1.05rem" }}>
            Authentic Konkani hospitality, peaceful short-term accommodations, and homemade coastal cuisine.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="container flex-grow-1 py-5">
        <div
          className="mx-auto p-4 p-md-5 rounded-4 shadow-sm bg-white border"
          style={{ maxWidth: "900px" }}
        >
          {/* Section 1: Introduction */}
          <div className="d-flex align-items-center gap-2 mb-4 text-danger">
            <FaHome className="fs-3" />
            <h2 className="h4 fw-bold mb-0 text-dark font-heading">Welcome to Our Coastal Homestay</h2>
          </div>

          <p className="text-secondary mb-4" style={{ lineHeight: "1.8", fontSize: "1.05rem" }}>
            <strong>{hotelName}</strong> is a dedicated holiday-home and short-term accommodation property situated in the tranquil beach town of <strong>Shrivardhan, Maharashtra</strong>. We offer comfortable, clean, and air-conditioned guest rooms tailored specifically for traveling families, couples, and friends seeking a peaceful coastal retreat just moments from the Arabian Sea.
          </p>

          <div className="alert alert-light border rounded-4 p-4 mb-4" style={{ background: "#f8fafc" }}>
            <div className="d-flex align-items-start gap-3">
              <FaShieldAlt className="text-success fs-4 mt-1 flex-shrink-0" />
              <div>
                <strong className="d-block text-dark mb-1">Direct Property Operator Note:</strong>
                <p className="small text-muted mb-0" style={{ lineHeight: "1.6" }}>
                  Ganesh Holiday Homes is a physical, owner-operated lodging property. We are <strong>not</strong> an intermediary travel agency, tour package aggregator, or ticketing portal. All room bookings made through this platform are fulfilled directly by our on-site property staff.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          {/* Section 2: Why Stay With Us Grid */}
          <h3 className="h5 fw-bold text-dark mb-3 font-heading">Why Guests Choose Ganesh Holiday Homes</h3>
          <div className="row g-3 mb-4">
            <div className="col-12 col-md-6">
              <div className="p-4 bg-light rounded-4 border h-100">
                <div className="d-flex align-items-center gap-2 text-danger mb-2">
                  <FaUmbrellaBeach className="fs-5" />
                  <strong className="text-dark">5-Minute Walk to Beach</strong>
                </div>
                <p className="text-secondary small mb-0" style={{ lineHeight: "1.6" }}>
                  Located just 500 meters from Shrivardhan Beach, allowing guests easy walking access to morning walks, golden sunsets, and beach water sports without needing to drive.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="p-4 bg-light rounded-4 border h-100">
                <div className="d-flex align-items-center gap-2 text-danger mb-2">
                  <FaUtensils className="fs-5" />
                  <strong className="text-dark">Homemade Konkani Cuisine</strong>
                </div>
                <p className="text-secondary small mb-0" style={{ lineHeight: "1.6" }}>
                  Freshly prepared traditional coastal recipes including fresh Surmai & Pomfret fish fry, Solkadhi, Ukadiche Modak, and wholesome vegetarian thalis cooked fresh upon advance request.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="p-4 bg-light rounded-4 border h-100">
                <div className="d-flex align-items-center gap-2 text-danger mb-2">
                  <FaShieldAlt className="fs-5" />
                  <strong className="text-dark">Safe & Gated Family Stay</strong>
                </div>
                <p className="text-secondary small mb-0" style={{ lineHeight: "1.6" }}>
                  Peaceful private compound with gated car parking, 24/7 power backup, and quiet surroundings away from highway noise.
                </p>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div className="p-4 bg-light rounded-4 border h-100">
                <div className="d-flex align-items-center gap-2 text-danger mb-2">
                  <FaCheckCircle className="fs-5" />
                  <strong className="text-dark">Clean, Spacious Accommodations</strong>
                </div>
                <p className="text-secondary small mb-0" style={{ lineHeight: "1.6" }}>
                  Choice of Standard AC, Budget Non-AC, and Double Bed Family Suites equipped with attached private bathrooms, hot water geysers, and high-speed Wi-Fi.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          {/* Section 3: Physical Location */}
          <h3 className="h5 fw-bold text-dark mb-2 font-heading">Our Physical Location</h3>
          <p className="text-secondary small mb-3">
            <FaMapMarkerAlt className="text-danger me-1" />
            <strong>Address:</strong> {contact.address}
          </p>
          <p className="text-secondary small mb-4" style={{ lineHeight: "1.6" }}>
            Shrivardhan is a historic coastal gem in the Konkan region of Maharashtra, easily accessible from Mumbai (approx. 4 hours via NH 66) and Pune (approx. 3.5 hours via Tamhini Ghat).
          </p>

          <div className="text-center pt-3 border-top d-flex flex-column flex-sm-row gap-2 justify-content-center">
            <Link
              to="/rooms"
              className="btn btn-outline-danger rounded-pill px-4 py-2 small fw-semibold"
            >
              Explore Our Rooms & Tariffs
            </Link>
            <Link
              to="/booking"
              className="btn btn-danger rounded-pill px-4 py-2 small fw-semibold d-inline-flex align-items-center justify-content-center gap-2 shadow-sm"
            >
              <FaCalendarAlt /> Reserve Room Online
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutUs;
